import { Moon, Sun, Monitor } from "lucide-react";
import { Button } from "./button";
import { useTheme } from "../../contexts/ThemeContext";
import { cn } from "../../lib/utils";
import { useState, useRef, useEffect } from "react";

/**
 * Theme toggle button component
 *
 * Provides options to select:
 * - Light mode
 * - Dark mode
 * - System preference
 *
 * @example
 * ```tsx
 * <ThemeToggle />
 * ```
 */
export function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="relative" ref={menuRef}>
      <Button
        variant="outline"
        size="icon"
        className="relative"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle theme"
        aria-expanded={isOpen}
      >
        <Sun className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
        <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-36 rounded-md border bg-popover p-1 shadow-md z-50">
          <button
            onClick={() => {
              setTheme("light");
              setIsOpen(false);
            }}
            className={cn(
              "flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent transition-colors",
              theme === "light" && "bg-accent"
            )}
          >
            <Sun className="mr-2 h-4 w-4" />
            <span>Light</span>
          </button>
          <button
            onClick={() => {
              setTheme("dark");
              setIsOpen(false);
            }}
            className={cn(
              "flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent transition-colors",
              theme === "dark" && "bg-accent"
            )}
          >
            <Moon className="mr-2 h-4 w-4" />
            <span>Dark</span>
          </button>
          <button
            onClick={() => {
              setTheme("system");
              setIsOpen(false);
            }}
            className={cn(
              "flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent transition-colors",
              theme === "system" && "bg-accent"
            )}
          >
            <Monitor className="mr-2 h-4 w-4" />
            <span>System</span>
          </button>
        </div>
      )}
    </div>
  );
}

